class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.updateQuantity(this.dataset.index, 0, event);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  // The cart page (<cart-items>) and the header drawer (<cart-drawer-items>) both live on /cart.
  // Each publishes with its OWN source so a change made in one re-renders the other; only the
  // element that made the change skips the refresh (it already painted the returned sections).
  get sourceName() {
    return this.tagName === 'CART-DRAWER-ITEMS' ? 'cart-drawer-items' : 'cart-items';
  }

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === this.sourceName) {
        return;
      }
      return this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  // Page inputs are #Quantity-N, drawer inputs are #Drawer-quantity-N — always look inside `this`
  // so the drawer never touches the page's inputs (and vice versa).
  getQuantityInput(id) {
    return this.querySelector(`#Quantity-${id}, #Drawer-quantity-${id}`);
  }

  resetQuantityInput(id) {
    const input = this.getQuantityInput(id);
    if (input) input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    let message = '';

    if (inputValue < event.target.dataset.min) {
      message = window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min);
    } else if (inputValue > parseInt(event.target.max)) {
      message = window.quickOrderListStrings.max_error.replace('[max]', event.target.max);
    } else if (inputValue % parseInt(event.target.step) !== 0) {
      message = window.quickOrderListStrings.step_error.replace('[step]', event.target.step);
    }

    if (message) {
      this.setValidity(event, index, message);
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.updateQuantity(
        index,
        inputValue,
        event,
        document.activeElement.getAttribute('name'),
        event.target.dataset.quantityVariantId
      );
    }
  }

  onChange(event) {
    this.validateQuantity(event);
  }

  // Re-render this element from a fresh server render after ANOTHER component changed the cart.
  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      // Swap the whole drawer panel (items, milestone bar, footer, empty state) and sync the
      // is-empty flag — the same surface the drawer paints for its own changes.
      return fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceDrawer = html.querySelector('cart-drawer');
          const targetDrawer = document.querySelector('cart-drawer');
          const sourceInner = sourceDrawer && sourceDrawer.querySelector('.drawer__inner');
          const targetInner = targetDrawer && targetDrawer.querySelector('.drawer__inner');
          if (!sourceInner || !targetInner) return;
          targetDrawer.classList.toggle('is-empty', sourceDrawer.classList.contains('is-empty'));
          targetInner.innerHTML = sourceInner.innerHTML;
        })
        .catch((e) => {
          console.error(e);
        });
    }

    // Cart page: items + order-summary footer, plus their empty-state flags.
    const itemsWrapper = document.getElementById('main-cart-items');
    const footer = document.getElementById('main-cart-footer');
    const sectionIds = [itemsWrapper && itemsWrapper.dataset.id, footer && footer.dataset.id].filter(Boolean);
    if (!sectionIds.length) return Promise.resolve();

    return fetch(`${routes.cart_url}?sections=${sectionIds.join(',')}`)
      .then((response) => response.json())
      .then((sections) => {
        if (itemsWrapper && sections[itemsWrapper.dataset.id]) {
          const source = new DOMParser()
            .parseFromString(sections[itemsWrapper.dataset.id], 'text/html')
            .querySelector('cart-items');
          if (source) {
            this.className = source.className;
            this.innerHTML = source.innerHTML;
          }
        }
        if (footer && sections[footer.dataset.id]) {
          const source = new DOMParser()
            .parseFromString(sections[footer.dataset.id], 'text/html')
            .getElementById('main-cart-footer');
          if (source) {
            footer.className = source.className;
            footer.innerHTML = source.innerHTML;
          }
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      },
    ];
  }

  updateQuantity(line, quantity, event, name, variantId) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });
    const eventTarget = event.currentTarget instanceof CartRemoveButton ? 'clear' : 'change';

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);

        CartPerformance.measure(`${eventTarget}:paint-updated-sections"`, () => {
          const quantityElement = this.getQuantityInput(line);
          const items = this.querySelectorAll('.cart-item');

          if (parsedState.errors) {
            if (quantityElement) quantityElement.value = quantityElement.getAttribute('value');
            this.updateLiveRegions(line, parsedState.errors);
            return;
          }

          this.classList.toggle('is-empty', parsedState.item_count === 0);
          const cartDrawerWrapper = document.querySelector('cart-drawer');
          const cartFooter = document.getElementById('main-cart-footer');

          if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
          if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

          this.getSectionsToRender().forEach((section) => {
            const container = document.getElementById(section.id);
            const sectionHtml = parsedState.sections && parsedState.sections[section.section];
            if (!container || !sectionHtml) return;
            const elementToReplace = container.querySelector(section.selector) || container;
            const source = new DOMParser().parseFromString(sectionHtml, 'text/html').querySelector(section.selector);
            if (source) elementToReplace.innerHTML = source.innerHTML;
          });
          const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
          let message = '';
          if (
            quantityElement &&
            items.length === parsedState.items.length &&
            updatedValue !== parseInt(quantityElement.value)
          ) {
            if (typeof updatedValue === 'undefined') {
              message = window.cartStrings.error;
            } else {
              message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
            }
          }
          this.updateLiveRegions(line, message);

          // Focus management. Must never throw: anything thrown here rejects the chain and skips the
          // publish() below, leaving the other cart view (page/drawer) stale.
          const inDrawer = this.tagName === 'CART-DRAWER-ITEMS';
          const lineItem = this.querySelector(`#CartItem-${line}, #CartDrawer-Item-${line}`);
          const focusTarget = lineItem && name ? lineItem.querySelector(`[name="${name}"]`) : null;
          if (focusTarget) {
            inDrawer && cartDrawerWrapper ? trapFocus(cartDrawerWrapper, focusTarget) : focusTarget.focus();
          } else if (inDrawer && cartDrawerWrapper && parsedState.item_count === 0) {
            const emptyContainer = cartDrawerWrapper.querySelector('.drawer__inner-empty');
            if (emptyContainer) trapFocus(emptyContainer, emptyContainer.querySelector('a'));
          } else if (inDrawer && cartDrawerWrapper && this.querySelector('.cart-item__name')) {
            trapFocus(cartDrawerWrapper, this.querySelector('.cart-item__name'));
          }
        });

        CartPerformance.measureFromEvent(`${eventTarget}:user-action`, event);

        publish(PUB_SUB_EVENTS.cartUpdate, { source: this.sourceName, cartData: parsedState, variantId: variantId });
      })
      .catch(() => {
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  
  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').textContent = message;

    if (this.lineItemStatusElement) this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      this.querySelector('#cart-live-region-text, #CartDrawer-LiveRegionText') ||
      document.getElementById('cart-live-region-text') ||
      document.getElementById('CartDrawer-LiveRegionText');
    if (!cartStatus) return;
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  // The items wrapper belonging to THIS element (page: #main-cart-items, drawer: #CartDrawer-CartItems).
  getItemsWrapper() {
    return this.querySelector('#main-cart-items, #CartDrawer-CartItems');
  }

  enableLoading(line) {
    const mainCartItems = this.getItemsWrapper();
    if (mainCartItems) mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    if (document.activeElement) document.activeElement.blur();
    if (this.lineItemStatusElement) this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = this.getItemsWrapper();
    if (mainCartItems) mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } })
              .then(() => CartPerformance.measureFromEvent('note-update:user-action', event));
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}

// Drawer "−" buttons (.qty-minus). Delegated so it keeps working after the drawer re-renders, and
// scoped to the button's own <cart-items>/<cart-drawer-items> so it never edits the page's inputs.
// A button wrapped in <quantity-input> is already stepped by global.js (which fires `change` →
// updateQuantity), so we only step manually when that wrapper is missing.
document.addEventListener('click', (e) => {
  const btn = e.target.closest('.qty-minus');
  if (!btn || btn.closest('quantity-input')) return;

  const cartItems = btn.closest('cart-drawer-items') || btn.closest('cart-items');
  if (!cartItems) return;

  const index = btn.dataset.index;
  const input = cartItems.getQuantityInput(index);
  const currentValue = input ? parseInt(input.value, 10) || 0 : 0;

  if (input && currentValue > 1) {
    input.value = currentValue - 1;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  } else {
    cartItems.updateQuantity(index, 0, e);
  }
});